<script lang="ts" setup>
import {
  computed,
  CSSProperties,
  onMounted,
  onUnmounted,
  PropType,
  ref,
} from "vue";
import type { ResizablePanelSizeSettings } from "./types";
import {
  useRootFontSize,
  useElementObserver,
  useMouseTracking,
} from "tir-pm-toolkit";

// #region defineProps
const props = defineProps({
  /**Настройки размера */
  sizeSettings: {
    type: Object as PropType<Partial<ResizablePanelSizeSettings>>,
    default: (): Partial<ResizablePanelSizeSettings> => ({}),
  },
  /**Разрешен ли рейсайз */
  allowResize: {
    type: Boolean,
    default: true,
  },
  /**Позиция контрола ресайза */
  resizeControlPosition: {
    type: String as PropType<"left" | "right">,
    default: "left",
  },
  /**Запоминать размер при закрытии */
  enableSaveSize: {
    type: Boolean,
    default: false,
  },
  /**Использовать v-show вместо v-if */
  useVShow: {
    type: Boolean,
    default: false,
  },
  /**Фиксированное позиционирование */
  isFixed: {
    type: Boolean,
    default: false
  }
});
// #endregion defineProps

// #region defineEmits
const emit = defineEmits<{
  /**Событие при закрытии панели */
  onClose: [];
  /**Событие при открытии панели */
  onOpen: [];
}>();
// #endregion defineEmits

// #region Константы
/**Настройки размера по умолчанию */
const DEFAULT_SIZE_SETTINGS: ResizablePanelSizeSettings = {
  initialWidth: 300,
  initialHeight: 400,
  minHeight: 100,
  minWidth: 100,
  maxHeight: 700,
  maxWidth: 700,
};
// #endregion Константы

// #region refs
/**В состоянии изменения размера */
const isResizing = ref(false);
/**Ссылка на target элемент */
const $target = ref<HTMLElement>();
/**Ссылка на элемент контейнера */
const $container = ref<HTMLElement>();
/**Отслеживание перемещение мыши по экрану */
const { start, stop, offset: mouseOffset, isTracking } = useMouseTracking();
/**Размер корневого шрифта */
const { rootFontSizePx } = useRootFontSize();
/**Размеры body элемента */
const { size: bodySize } = useElementObserver(document.body);
/**Обсервер */
const {
  size: targetSize,
  position: targetPosition,
  updatePositionAndSize,
} = useElementObserver($target);
/**Внутренние настройки размера */
const innerSizeSettings = computed<ResizablePanelSizeSettings>(() =>
  mergeSizeSettings(props.sizeSettings)
);
/**Ширина */
const width = ref(
  addWidthOffset(
    innerSizeSettings.value.initialWidth,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  )
);
/**Высота */
const height = ref(
  addHeightOffset(
    innerSizeSettings.value.initialHeight,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  )
);
/**Видимость */
const visible = ref(false);
// #endregion refs

// #region computed
const style = computed<CSSProperties>(() => {
  const _width = isTracking.value
    ? addWidthOffset(
        width.value,
        mouseOffset.value.x,
        innerSizeSettings.value.minWidth,
        innerSizeSettings.value.maxWidth
      )
    : width.value;

  const _height = isTracking.value
    ? addHeightOffset(
        height.value,
        mouseOffset.value.y,
        innerSizeSettings.value.minHeight,
        innerSizeSettings.value.maxHeight
      )
    : height.value;

  const left = Math.max(
    Math.min(targetPosition.value.left, bodySize.value.width - _width),
    0
  );

  const right = Math.min(
    targetPosition.value.right,
    bodySize.value.width - _width
  );

  return {
    top: `calc(${convertToRem(
      targetPosition.value.top + targetSize.value.height,
      rootFontSizePx.value
    )}rem + var(--tir-pm-resizable-panel__top-indent))`,
    left:
      props.resizeControlPosition === "right"
        ? `${convertToRem(left, rootFontSizePx.value)}rem`
        : undefined,
    right:
      props.resizeControlPosition === "left"
        ? `${convertToRem(right, rootFontSizePx.value)}rem`
        : undefined,
    width: `${convertToRem(_width, rootFontSizePx.value)}rem`,
    height: `${convertToRem(_height, rootFontSizePx.value)}rem`,
  };
});
// #endregion computed

// #region Хуки жизненного цикла
onMounted(() => {
  toggleEvents(true);
});

onUnmounted(() => {
  toggleEvents(false);
});
// #endregion Хуки жизненного цикла

// #region Функции
/**Слияние настроек размера */
function mergeSizeSettings(
  customSettings?: Partial<ResizablePanelSizeSettings>
): ResizablePanelSizeSettings {
  return {
    ...DEFAULT_SIZE_SETTINGS,
    ...customSettings,
  };
}
/**Конвертировать пиксели в rem */
function convertToRem(px: number, basePx: number) {
  return px / basePx;
}

/**
 * Добавить смещение в высоту
 * @param currentHeight Текущая высота
 * @param offsetY Смещение по Y
 * @param min Минимальная высота
 * @param max Максимальная высота
 */
function addHeightOffset(
  currentHeight: number,
  offsetY: number,
  min: number,
  max: number
) {
  let _height = currentHeight + offsetY;

  _height = Math.max(_height, min);
  _height = Math.min(_height, max);

  return _height;
}

/**
 * Добавить смещение в ширину
 * @param currentWidth Текущая ширина
 * @param offsetX Смещение по X
 * @param min Минимальная ширина
 * @param max Максимальная ширина
 */
function addWidthOffset(
  currentWidth: number,
  offsetX: number,
  min: number,
  max: number
) {
  let _width =
    props.resizeControlPosition === "left"
      ? currentWidth - offsetX
      : currentWidth + offsetX;

  _width = Math.max(_width, min);
  _width = Math.min(_width, max);

  return _width;
}

/**Переключить класс body при ресайзе */
function toggleResizeBodyClass() {
  const className =
    props.resizeControlPosition === "left"
      ? "tir-pm-resizable-panel__resize-left"
      : "tir-pm-resizable-panel__resize-right";
  if (document.body.classList.contains(className)) {
    document.body.classList.remove(className);
  } else {
    document.body.classList.add(className);
  }
}

/**
 * Проверить клик вне области
 * @param event
 */
function checkClickOutside(event: MouseEvent | TouchEvent) {
  const target = getTargetByEvent(event);
  if (!target) return;

  if (
    visible.value &&
    !$target.value?.contains(target as Node) &&
    !$container.value?.contains(target as Node)
  )
    close();
}
/**Получить target элемент */
function getTargetByEvent(event: MouseEvent | TouchEvent) {
  return event instanceof MouseEvent ? event.target : event.touches[0]?.target;
}
/**При событии mouseup */
function onMouseUp(event: MouseEvent | TouchEvent) {
  // Если не в состоянии перетаскивания проверяем клик вне области
  if (!isResizing.value) {
    checkClickOutside(event);
    return;
  } else {
    stopResize();
  }
}
/**Завершить изменени размера */
function stopResize() {
  if (!isResizing.value) return;
  isResizing.value = false;

  const _width = addWidthOffset(
    width.value,
    mouseOffset.value.x,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );
  const _height = addHeightOffset(
    height.value,
    mouseOffset.value.y,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );
  stop();
  width.value = _width;
  height.value = _height;
  toggleResizeBodyClass();
}
/**Начать изменение размера */
function startResize(event: MouseEvent | TouchEvent) {
  if (isResizing.value) return;
  isResizing.value = true;
  start(event);
  toggleResizeBodyClass();
}
/**
 * Подписаться/отсписаться от событий
 * @param isSubscribe Подписаться
 */
function toggleEvents(isSubscribe: boolean) {
  const fnName = isSubscribe ? "addEventListener" : "removeEventListener";
  window[fnName]("mouseup", onMouseUp);
  window[fnName]("touchend", onMouseUp);
}
/**Сбросить размер */
function resetSize() {
  width.value = addWidthOffset(
    innerSizeSettings.value.initialWidth,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );
  height.value = addHeightOffset(
    innerSizeSettings.value.initialHeight,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );
}
/**Открыть панель */
const open = () => {
  visible.value = true;

  width.value = addWidthOffset(
    width.value,
    0,
    innerSizeSettings.value.minWidth,
    innerSizeSettings.value.maxWidth
  );

  height.value = height.value = addHeightOffset(
    height.value,
    0,
    innerSizeSettings.value.minHeight,
    innerSizeSettings.value.maxHeight
  );

  emit("onOpen");
};
/**Закрыть панель */
const close = () => {
  if (visible.value) {
    visible.value = false;
    if (!props.enableSaveSize) {
      resetSize();
    }
    emit("onClose");
  }
};
// #endregion Функции

// #region defineExpose
defineExpose({
  /**Открыть панель */
  open,
  /**Закрыть панель */
  close,
  /**Обновить позицию вручную */
  updatePosition: updatePositionAndSize,
  /**Видимость */
  visible
});
// #endregion defineExpose
</script>

<template>
  <div class="tir-pm-resizable-panel">
    <div class="tir-pm-resizable-panel__target" ref="$target">
      <slot :open="open" :close="close" :visible="visible" />
    </div>
    <Teleport to="body">
      <Transition name="resizable-panel-transition">
        <div
          v-if="useVShow || visible"
          v-show="!useVShow || visible"
          ref="$container"
          :class="['tir-pm-resizable-panel__container', {'is-fixed': isFixed}]"
          :style="style"
        >
          <div class="tir-pm-resizable-panel__content">
            <slot name="content" :open="open" :close="close" />
          </div>
          <div
            :class="[
              'tir-pm-resizable-panel__resize-control',
              `position--${resizeControlPosition}`,
            ]"
            @mousedown="startResize"
            @touchstart="startResize"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.tir-pm-resizable-panel {
  &__container {
    position: absolute;
    z-index: var(--tir-pm-resizable-panel__z-index);
    box-shadow: var(--tir-pm-resizable-panel__shadow);
    overflow: hidden;
    border-radius: var(--tir-pm-resizable-panel__br);
    border: var(--tir-pm-resizable-panel__border);
    background-color: var(--tir-pm-resizable-panel__bg);

    &.is-fixed {
      position: fixed;
    }
  }

  &__content {
    height: 100%;
  }

  &__resize-control {
    position: absolute;
    bottom: 0;
    z-index: var(--tir-pm-resizable-panel__resize-control-z-index);
    width: var(--tir-pm-resizable-panel__resize-control-size);
    height: var(--tir-pm-resizable-panel__resize-control-size);
    background-color: var(--tir-pm-resizable-panel__resize-control-bg);

    &.position--left {
      left: 0;
      transform: translateX(-50%) translateY(50%) rotate(45deg);
      cursor: nesw-resize;
    }
    &.position--right {
      right: 0;
      transform: translateX(50%) translateY(50%) rotate(45deg);
      cursor: nwse-resize;
    }
  }
}

.resizable-panel-transition-enter-active,
.resizable-panel-transition-leave-active {
  transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.resizable-panel-transition-enter-from,
.resizable-panel-transition-leave-to {
  transform: translateY(-0.25rem);
  opacity: 0;
}
</style>

<style lang="scss">
.tir-pm-resizable-panel__resize-left {
  user-select: none !important;
  cursor: nesw-resize !important;

  * {
    user-select: none !important;
    cursor: nesw-resize !important;
  }
}
.tir-pm-resizable-panel__resize-right {
  user-select: none !important;
  cursor: nwse-resize !important;

  * {
    user-select: none !important;
    cursor: nwse-resize !important;
  }
}
</style>
